const BigNumber = require('bignumber.js');
const DocumentTypeEnum = require('@/lib/Orders/DocumentNumbers/DocumentTypeEnum');
const LocalStorageKeys = require('@/lib/Storage/LocalStorageKeys');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const MellonWebECRClient = require('@/lib/PaymentMethods/MellonWebECR/MellonWebECRClient');
const MellonWebECRPayment = require('@/lib/PaymentMethods/MellonWebECR/MellonWebECRPayment');
const MellonWebECRTransactionType = require('@/lib/PaymentMethods/MellonWebECR/Enums/MellonWebECRTransactionType');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const SignPaymentRequest = require('@/lib/Invoicing/EInvoicingSignPaymentRequest');
const { generateOrderInvoiceID } = require('@/lib/Invoicing');
const { getDocumentNumberFromOrder } = require('@/lib/Orders/DocumentNumbers');
const { getJwtExpiry } = require('@/functions/jwt');

/**
 * @typedef {object} MellonWebECRCompanyConfig
 * @property {string} apiUrl
 * @property {MellonWebECRApiKeyData[]} apiKeys
 */

/**
 * @typedef {object} MellonWebECRDeviceConfig
 * @property {string} apiKey - API key for the selected terminal.
 * @property {string} terminalID - The ID of the terminal to use with this device.
 * @property {string} terminalTerminalID - The terminal ID of the terminal to use with this device.
 *     Note that Mellon terminals return both an ID and a TerminalID and we need both of them.
 */

/**
 * @typedef {object} MellonWebECRApiKeyData
 * @property {string} apiKey
 * @property {string} label
 * @property {string} redeemedAt - ISO 8601 date string when the code was redeemed.
 */

/**
 * @typedef {object} MellonWebECRPaymentTaxBreakdown
 * @property {string} gross
 * @property {string} tax
 * @property {string} net
 */

/**
 * @typedef {object} MellonWebECRAccessTokenDetails
 * @property {string} token
 * @property {Date} expiresAt
 * @property {boolean} requiresRefresh
 */

class MellonWebECRDriver {
    /**
     * @param {AppConfig} appConfig
     * @param {CartService} cartService
     * @param {CurrentDevice} currentDevice
     * @param {EInvoicingService} eInvoicingService
     * @param {LocalStorageDataStore} localStorageDataStore
     * @param {PaymentMethodService} paymentMethodService
     * @param {ToastFactory} toastFactory
     * @param {ThirdPartyRefGenerator} thirdPartyRefGenerator
     */
    constructor(
        appConfig,
        cartService,
        currentDevice,
        eInvoicingService,
        localStorageDataStore,
        paymentMethodService,
        toastFactory,
        thirdPartyRefGenerator,
    ) {
        this.appConfig = appConfig;
        this.cartService = cartService;
        this.currentDevice = currentDevice;
        this.eInvoicingService = eInvoicingService;
        this.localStorageDataStore = localStorageDataStore;
        this.paymentMethodService = paymentMethodService;
        this.toastFactory = toastFactory;
        this.thirdPartyRefGenerator = thirdPartyRefGenerator;

        /**
         * @type {?string}
         */
        this.accessToken = null;

        /**
         * @type {string}
         */
        this.apiUrl = '';

        /**
         * @type {MellonWebECRClient}
         */
        this.client = null;

        this.log = logFactory('MellonWebECRDriver');

        /**
         * See comment in createTransactionRequest()
         *
         * @type {number}
         */
        this.requestTimeout = 120;
    }

    async initialize() {
        const spinner = this.toastFactory.spinner('MELLON_WEBECR.INIT');

        try {
            // Create a MellonWebECRClient instance.
            this.client = this.createClient();

            // Load the company and device config.
            // This will set the API key on the client.
            await this.loadConfig();

            // Load an existing access token from local storage, or grab a new one.
            // This will set the Access Token on the client.
            await this.checkAccessToken();

            // Set an interval to check the access token every 30 minutes.
            setInterval(() => {
                this.checkAccessToken();
            }, 30 * 60 * 1000);

            this.toastFactory.success('MELLON_WEBECR.INIT_SUCCESS');
        } catch (e) {
            this.log.error(e);
            this.toastFactory.errorTop(
                'MELLON_WEBECR.INIT_ERROR',
                e.message,
            );
        } finally {
            this.toastFactory.clear(spinner);
        }
    }

    /**
     * If we have no saved access token, call the eInvoicing service to generate one.
     * If we have one, check if it is expired (or expiring within 90 minutes) with the parseAccessToken() method.
     * If it is, call the eInvoicing service to "refresh" it. This doesn't actually use the refresh token, it just
     * calls the eInvoicing service again to get a new access token because why bother with the refresh token when
     * we can just get a new one.
     *
     * @param {boolean} forceRefresh - If true, always refresh the access token.
     *
     * @returns {Promise<MellonWebECRAccessTokenDetails>}
     */
    async checkAccessToken(forceRefresh = false) {
        const existingToken = this.localStorageDataStore.find(LocalStorageKeys.MELLON_WEBECR_ACCESS_TOKEN);
        if (!forceRefresh && existingToken) {
            try {
                const result = this.parseAccessToken(existingToken);
                if (result && !result.requiresRefresh) {
                    this.log.debug('Existing access token is valid and does not need refreshing. Expires at:', result.expiresAt);
                    this.setAccessToken(existingToken);

                    return result;
                }
            } catch (e) {
                // The saved token is a dud. Generate a new one.
            }
        }

        // If we get here, we need to generate a new token.
        const newToken = await this.generateAccessToken();
        const result = this.parseAccessToken(newToken);
        this.log.debug('New access token generated. Expires at:', result.expiresAt);
        this.setAccessToken(newToken);

        return result;
    }

    /**
     * @param {string} token
     */
    setAccessToken(token) {
        this.accessToken = token;
        this.client.setAccessToken(token);
        this.localStorageDataStore.persist(LocalStorageKeys.MELLON_WEBECR_ACCESS_TOKEN, token);
    }

    /**
     * @param {string} token
     *
     * @returns {MellonWebECRAccessTokenDetails}
     */
    parseAccessToken(token) {
        const expiresAt = getJwtExpiry(token);

        // If the token expires in less than 90 minutes we should refresh it.
        const requiresRefresh = (expiresAt - new Date()) < 90 * 60 * 1000;

        return {
            token,
            expiresAt,
            requiresRefresh,
        };
    }

    /**
     * See
     * https://digitickets.atlassian.net/wiki/spaces/DKB/pages/3942809620/Mellon+WebECR+Payment+Method+Integration#Authentication
     *
     * @returns {Promise<string>}
     */
    async generateAccessToken() {
        return this.eInvoicingService.axios
            .post(
                'mellon-webecr/auth',
            )
            .then(({ data }) => {
                return data.access;
            })
            .catch((error) => {
                throw new Error('Failed to generate an access token. ' + error.message);
            });
    }

    /**
     * @returns {MellonWebECRClient}
     */
    createClient() {
        return new MellonWebECRClient(
            this.appConfig,
        );
    }

    /**
     * @private
     *
     * Validate both the company and device level Mellon configs.
     */
    loadConfig() {
        let companyConfig = null;
        try {
            companyConfig = this.getPaymentMethod().getConfig();
        } catch (e) {
            throw new Error('The company MellonWebECR config is not valid JSON.');
        }

        if (!companyConfig?.apiUrl) {
            throw new Error('The company MellonWebECR config is missing the apiUrl property.');
        }
        this.client.setApiUrl(companyConfig.apiUrl);

        // What we need now is all stored in the device config.
        /**
         * @type {MellonWebECRDeviceConfig}
         */
        const deviceConfig = this.currentDevice.device.getPaymentTerminalConfigForMethod(
            PaymentMethodRef.CARD_MELLON_WEBECR,
        );
        this.log.debug('Device config', deviceConfig);

        if (!deviceConfig) {
            throw new Error('This device has no MellonWebECR config. Please configure it from the settings page.');
        }

        if (!deviceConfig.apiKey) {
            throw new Error("No apiKey found in the device's MellonWebECR config. Please configure it from the settings page.");
        }
        this.client.setApiKey(deviceConfig.apiKey);

        if (!deviceConfig.terminalID) {
            throw new Error("No terminalID found in the device's MellonWebECR config. Please configure it from the settings page.");
        }

        if (!deviceConfig.terminalTerminalID) {
            throw new Error("No terminalTerminalID found in the device's MellonWebECR config. Please configure it from the settings page.");
        }
    }

    /**
     * @returns {PaymentMethod}
     */
    getPaymentMethod() {
        return this.paymentMethodService.getByRef(PaymentMethodRef.CARD_MELLON_WEBECR);
    }

    /**
     * @param {Order} order
     * @param {number} paymentAmount
     *
     * @returns {Promise<MellonWebECRPayment>}
     */
    async createNewPayment(order, paymentAmount) {
        const paymentMethod = this.getPaymentMethod();

        const thirdPartyID = this.generateThirdPartyID(order, paymentAmount);

        const payment = new MellonWebECRPayment({
            paymentMethod,
            thirdPartyID,
            amount: paymentAmount,
        });
        payment.setTendered(paymentAmount);

        return payment;
    }

    /**
     * Sends a request to the Entersoft eInvoicing service to create a signature for the payment, and a payment ID,
     * needed to unlock the card machine. That payment ID must be used in the subsequent Entersoft invoice.
     *
     * @param {Order} order
     * @param {MellonWebECRPayment} payment
     * @param {string} terminalID
     * @param {TaxSummary} taxSummary
     */
    async signPayment(
        order,
        payment,
        terminalID,
        taxSummary,
    ) {
        const taxBreakdown = this.getTaxAmountsForPayment(payment.amount, taxSummary);
        payment.taxBreakdown = taxBreakdown;

        // The externalID of the payment must match the SupplierUID of the invoice that is later sent to Entersoft.
        // The supplierUID is a format determined by us.
        // {bookingRef}-invoice-{invoiceIndex}
        // or
        // {bookingRef}-credit-{creditNoteIndex}
        // For the time being, the invoiceIndex and creditNoteIndex will always be 1, because we don't support
        // more than one invoice or credit note per order.

        // With the index being the index of the invoice or credit note in the list of all the booking's invoices
        // or credit notes. Since we currently only support one invoice and credit note per order
        // we can use 1 for both.
        // This won't be correct if we allow zero value adjustments too. But it isn't actually used for
        // anything yet so 1 is fine.

        const {
            externalID,
            myDataType,
        } = this.generateExternalID(order, payment.amount);

        /**
         * @type {DocumentNumber}
         */
        const documentNumber = getDocumentNumberFromOrder(order, DocumentTypeEnum.GREEK_TXN_NUMBER_PROPOINT);
        if (!documentNumber) {
            throw new Error('Order does not have a GREEK_TXN_NUMBER_PROPOINT document number. Cannot sign a payment without it.');
        }

        const request = new SignPaymentRequest(
            externalID,
            new BigNumber(taxBreakdown.gross).abs().toNumber(),
            new BigNumber(taxBreakdown.tax).abs().toNumber(),
            new BigNumber(taxBreakdown.net).abs().toNumber(),
            new BigNumber(payment.amount).abs().toNumber(),
            terminalID,
            documentNumber.number.toString(),
            documentNumber.documentNumberSequencePrefix,
            myDataType,
        );

        const response = await this.eInvoicingService.signPayment(request);

        /**
         * @type {EInvoicingSignPaymentResult}
         */
        const signResult = {
            uid: response.entersoftResponse.pnf.uid,
            pid: response.entersoftResponse.pid,
            sig: response.entersoftResponse.pnf.dsig.sv,
            tidnsp: response.entersoftResponse.pnf.tid_nsp,
            sigd: response.entersoftResponse.pnf.dsig.sigd,
            extid: response.entersoftResponse.extid,
        };

        payment.signResponse = response;
        payment.setSignResult(signResult);
    }

    /**
     * @param {number} paymentAmount
     * @param {TaxSummary} taxSummary
     *
     * @returns {MellonWebECRPaymentTaxBreakdown}
     */
    getTaxAmountsForPayment(paymentAmount, taxSummary) {
        // The TaxSummary should contain a TaxSummaryGroup that is the sum of all the tax groups in the order.
        // From this we can easily know the total gross/tax/net amounts.
        let gross = taxSummary.taxSummarySumGroup.gross;
        let tax = taxSummary.taxSummarySumGroup.tax;
        let net = taxSummary.taxSummarySumGroup.net;

        // If we are taking one payment for the whole order this is easy - we have the numbers already.
        if (paymentAmount === net) {
            return {
                gross,
                tax,
                net,
            };
        }

        // Otherwise we are probably taking a partial payment.
        // What we'll do is calculate the ratio of the payment to the total order amount.
        // Then we'll apply that ratio to the gross/net/tax amounts to get the partial amounts.
        // Use BigNumber to avoid rounding issues.
        const ratio = new BigNumber(paymentAmount).div(gross);

        gross = new BigNumber(gross).times(ratio).toFixed(2);
        tax = new BigNumber(tax).times(ratio).toFixed(2);
        net = new BigNumber(net).times(ratio).toFixed(2);

        return {
            gross,
            tax,
            net,
        };
    }

    /**
     * @param {MellonWebECRPayment} payment
     *
     * @returns {MellonWebECRStartTransactionRequest}
     */
    createTransactionRequest(payment) {
        // All amounts should be positive numbers.

        return {
            TxnType: payment.amount < 0 ? MellonWebECRTransactionType.REFUND : MellonWebECRTransactionType.SALE,

            // The payable transaction amount in integer form. Ex. 1.00 eur is 100
            Amount: new BigNumber(payment.signResponse.signRequest.tpa)
                .multipliedBy(100)
                .abs() // Make sure it's positive.
                .toNumber(),

            // The ISO 4217 numerical currency code . 978 for EUR
            CurrencyCode: 978,

            CustomerEmail: this.cartService.currentCart.customer?.contact?.email || null,

            // A caller defined reference which can be used to reference the intent and eventually the
            // transaction. Maximum length of 50 characters
            CustomerReference: payment.thirdPartyID,

            // A data object representing the relevant data required by Greek law to accompany a provider
            // signature based transaction request.
            ProviderData: {
                // The UID of the payment, provided by Entersoft.
                Uid: payment.signResult.uid,

                Mark: null,

                // The generation timestamp of ProviderSignature in the same format as in the signature itself,
                // namely YYYYMMDDhhmmss in Greece local time.
                // The eInvoicing service provides this formatted already.
                SignatureTimestamp: payment.signResponse.mellonSignatureTimestamp,

                // AK: For the following 3 values (also for Amount above) we'll use the exact values
                // the eInvoicing service sent to Entersoft to sign the request. This is to minimize
                // any discrepancies that could cause issues with the signature validation process.

                // The price of goods without any VAT applied to it
                NetAmount: new BigNumber(payment.signResponse.signRequest.dinf.tna)
                    .multipliedBy(100)
                    .abs() // Make sure it's positive.
                    .toNumber(),

                // The amount of VAT that is applied to the net amount
                VatAmount: new BigNumber(payment.signResponse.signRequest.dinf.tva)
                    .multipliedBy(100)
                    .abs() // Make sure it's positive.
                    .toNumber(),

                // The total amount of the transaction
                TotalAmount: new BigNumber(payment.signResponse.signRequest.dinf.ta)
                    .multipliedBy(100)
                    .abs() // Make sure it's positive.
                    .toNumber(),

                // The id of the electronic invoicing provider. It is required for public key lookup.
                ProviderId: 2, // 2 = Entersoft.

                // The electronic invoicing provider signature.
                // Required when a provider signature validation process is used.
                Signature: payment.signResult.sig,
            },

            // By providing a Timeout value, the create transaction request will hang and will return a response
            // either when the transaction has finished, or when this timeout has been reached, whichever comes first.
            // After this, we can query the get transaction intent endpoint to get the status of the transaction.
            // Mellon say:
            // Your software's requests should have more than one second time interval and not happen indefinitely.
            // We suggest using values close to the maximum 180 (seconds) at 'Timeout' field to receive transaction
            // results in the response body of your txninit requests and reduce the need for additional
            // GET requests from your software.
            //
            // We're not using the full 180s here because in practise transactions will usually take much less time
            // than this. If it's taking this long something is probably wrong, and we don't want to waste an extra
            // minute.
            // After this timeout the user has the option to re-check or abort, so they can still process
            // transactions that take longer than this anyway.
            Timeout: this.requestTimeout,
        };
    }

    /**
     * @param {Order} order
     * @param {number} paymentAmount
     *
     * @returns {{externalID: string, myDataType: string}}
     */
    generateExternalID(order, paymentAmount) {
        let externalID;
        let myDataType;

        if (paymentAmount < 0) {
            // Negative payments should only be used where credit notes are being issued.

            // This also covers non-adjustment refund orders. In this case DigiTickets does not issue a credit note,
            // it issues a negative invoice. But ProPoint will tell the invoicing service to treat it as a credit note.
            // As the check here is simply if the payment amount is negative, this just works for both cases.
            externalID = generateOrderInvoiceID(order, true);
            myDataType = 'credit';
        } else {
            externalID = generateOrderInvoiceID(order);
            myDataType = 'invoice';
        }

        return {
            externalID,
            myDataType,
        };
    }

    /**
     * @param {Order} order
     * @param {number} paymentAmount
     *
     * @returns {string}
     */
    generateThirdPartyID(order, paymentAmount) {
        // The thirdPartyID is used as the CustomerReference in the transaction request.
        // We can send this to Mellon and use it to identify the transaction.

        // The obvious thought here is to use the normal thirdPartyID as the CustomerReference for the payment.
        // However, we need this to be more useful for something else.

        // When you sign a payment for Mellon using Entersoft, you need to supply Entersoft with an
        // "externalID". That externalID is not unique - it must be the ID of invoice that will be created that
        // this payment will appear on.
        // That externalID becomes part of the payment's signature, and Entersoft will check it when we send them an
        // invoice containing this payment.

        // The externalID will look like:
        // {bookingRef}-invoice-{invoiceIndex} or {bookingRef}-credit-{creditNoteIndex}
        // For now invoiceIndex and creditNoteIndex will always be 1.
        // For example:
        // D1234-invoice-1

        // When we start a transaction with Mellon we need to send the signature that Entersoft gave us.
        // But if we look at a list of transaction from Mellon, we can't get see signature again.

        // It can be very useful for debugging payment issues here to be able to see the externalID that was used
        // when signing this payment. The only field for the payment we can use for this is the CustomerReference.
        // We can send that with the transaction request, and it will be returned in the transaction response and
        // when listing transactions.

        // So lets use that externalID as the CustomerReference. It makes sense to also use it as the thirdPartyID
        // of the payment too, as our standard is to use the thirdPartyID as the reference sent to payment providers.

        // But wait, there's more!
        // The same externalID must be used for all payments on the same invoice (the same order).
        // However, the CustomerReference must be unique. So if we just use the externalID as-is that would prevent
        // multiple card payments on the same order, as the second one would not be unique.

        // So lets take the externalID and add the incrementing counter (cart.paymentsAttempted) normally used
        // as the last part of the thirdPartyID.

        // In the end the externalID will look like:
        // D1234-invoice-1
        // and the thirdPartyID / CustomerReference will look like:
        // D1234-invoice-1-1
        // D1234-invoice-1-2
        // or
        // D1234-credit-1-1 for refunds

        // Don't be fooled by the ExternalId field returned on Mellon transactions. I tried setting it, and
        // it doesn't do anything - it is always null. So we're stuck using the CustomerReference for this.

        const { externalID } = this.generateExternalID(order, paymentAmount);

        // Call the thirdPartyRefGenerator to generate a third party ID anyway because this will increment
        // the paymentsAttempted counter on the cart, which gets used in the customer reference.
        this.thirdPartyRefGenerator.generatePaymentThirdPartyRef(this.cartService.currentCart);

        return externalID + '-' + this.cartService.currentCart.paymentsAttempted;
    }

    /**
     * @param {number} terminalID
     * @param {number} transactionIntentID
     *
     * @returns {Promise<{success: boolean, response:MellonWebECRVoidTransactionResponse}>}
     */
    async voidTransaction(terminalID, transactionIntentID) {
        // Void can be called from either the recovery modal, or from the Angular list of payments on the left
        // of the payment modal. If the latter, we don't have much control over the UI.
        // So to make it nice, use a toast to show something is happening.
        const spinner = this.toastFactory.spinner('MELLON_WEBECR.VOIDING');

        return this.client
            .voidTransaction(
                terminalID,
                {
                    CustomerEmail: this.cartService.currentCart.customer?.contact?.email || null,
                    OriginalIdentifier: transactionIntentID,
                    OriginalIdentifierType: 1, // 1 = we are using the intent ID as the identifier

                    // See the comment about Timeout above in createTransactionRequest.
                    Timeout: this.requestTimeout,
                },
            )
            .then((response) => {
                this.log.debug('Void transaction response:', response);

                if (response.Transaction?.Voided) {
                    this.toastFactory.success('MELLON_WEBECR.VOID_SUCCESS');
                }

                return {
                    success: response.Transaction?.Voided,
                    response,
                };
            })
            .catch((error) => {
                console.error('Error voiding transaction:', error);

                // Display an error message to the user.
                this.toastFactory.errorTop(
                    'MELLON_WEBECR.VOID_ERROR',
                    error.message,
                );

                throw error;
            })
            .finally(() => {
                this.toastFactory.clear(spinner);
            });
    }
}

module.exports = MellonWebECRDriver;
